export interface ChatIframeOptions {
  sandbox?: string;
  allow?: string;
  className?: string;
}

/**
 * Creates and configures a chat iframe for embedding Walter chat.
 *
 * @param container - The HTML element to append the iframe to
 * @param url - The URL to load in the iframe
 * @param options - Optional iframe configuration
 * @returns The created iframe element
 */
export function createChatIframe(
  container: HTMLElement,
  url: string,
  options: ChatIframeOptions = {}
): HTMLIFrameElement {
  const iframe = document.createElement("iframe");

  iframe.src = url;
  iframe.style.width = "100%";
  iframe.style.height = "100%";
  iframe.style.border = "none";
  iframe.style.display = "block";

  // Set sandbox permissions - allow scripts, forms, same-origin, popups by default
  iframe.sandbox.add(
    "allow-scripts",
    "allow-same-origin",
    "allow-forms",
    "allow-popups",
    "allow-popups-to-escape-sandbox"
  );

  // Override with custom sandbox if provided
  if (options.sandbox) {
    iframe.setAttribute("sandbox", options.sandbox);
  }

  // Set allow attribute for permissions like clipboard access
  if (options.allow) {
    iframe.setAttribute("allow", options.allow);
  }

  if (options.className) {
    iframe.className = options.className;
  }

  container.appendChild(iframe);

  return iframe;
}

/**
 * Shows an iframe by removing the hidden class and setting display.
 */
export function showIframe(iframe: HTMLIFrameElement): void {
  iframe.classList.remove("hidden");
  iframe.style.display = "block";
}

/**
 * Hides an iframe by adding the hidden class and setting display.
 */
export function hideIframe(iframe: HTMLIFrameElement): void {
  iframe.classList.add("hidden");
  iframe.style.display = "none";
}

/**
 * Checks if iframe is currently visible.
 */
export function isIframeVisible(iframe: HTMLIFrameElement): boolean {
  return !iframe.classList.contains("hidden") && iframe.style.display !== "none";
}
